import { DocumentReference } from 'firebase/firestore';
import { EntityLabel, MetricGroup } from '@esg/esg-global-types';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { apollo_client } from '../app/db_util';
import { validateMasterListParams } from '../../util/validation';
import { auth } from '../google/firebase';
import {
  GET_METRIC_GROUP_QUERY,
  GET_METRIC_GROUPS_QUERY
} from '../graphql/queries/metric_capture/metric_group';
import {
  CREATE_METRIC_GROUP_MUTATOR,
  DELETE_METRIC_GROUP_WITH_METRICS_MUTATOR,
  UPDATE_METRIC_GROUP_MUTATOR
} from '../graphql/mutations/metric_capture/metric_group';
import { MetricExtended } from './metric';

export type MetricGroupData = Omit<MetricGroup, 'id'>;

export interface MetricGroupExtended extends Omit<MetricGroup, 'metrics'> {
  metrics: Array<MetricExtended>;
}

// Singular and plural label for model entity.
export const metric_group_label: EntityLabel = {
  one: 'Metric Group',
  many: 'Metric Groups'
};

/**
 * Create a collection path string for the master list or configured metric groups.
 * @param {boolean} master_list Reference the master list collection.
 * @param {string} group_id When not referencing a master list collection, a group id for the configured company is required.
 * @param {string} company_id When not referencing a master list collection, a company id for the configured company is required.
 * @returns {string} Path to a metric groups collection.
 */
export const createMetricGroupCollectionPath = (
  master_list: boolean,
  group_id?: string,
  company_id?: string
): string => {
  try {
    validateMasterListParams(master_list, group_id, company_id);
    return master_list
      ? `metric_group_master_list`
      : `groups/${group_id}/companies/${company_id}/metric_groups`;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getMetricGroupCollectionPath failed on an unknown error.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all metric group documents for current portal company joined with relative metric group data
 * @param {boolean} master_list Return master list or configured metric group documents.
 * @param {string} group_id Group object of current portal company
 * @param {string} company_id id of current portal company to reference firestore documents
 * @param {string} id query a specific Metric Group by id.
 * @returns {Promise<Array<MetricGroup>>}
 */
export const getMetricGroups = async (
  master_list: boolean,
  group_id?: string,
  company_id?: string,
  id?: string
): Promise<Array<MetricGroup>> => {
  try {
    const metric_groups: Array<MetricGroup> = await apollo_client
      .query({
        variables: {
          master_list: master_list,
          group_id: group_id,
          company_id: company_id,
          id: id
        },
        query: GET_METRIC_GROUPS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.metric_groups;
      });
    return metric_groups;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getMetricGroups failed on an unknown error.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query a metric group by name for either configured or master list metric groups.
 * @param {boolean} master_list Return master list or configured metric documents.
 * @param {string} metric_group_name Name of the metric's group.
 * @param {string} group_id When not referencing a master list document or collection, a group id for the configured company is required.
 * @param {string} company_id When not referencing a master list document or collection, a company id for the configured company is required.
 * @param {boolean} deleted Query only deleted or non-deleted metric groups. Defaults to both deleted and non-deleted metrics.
 * @returns {Promise<MetricGroup | undefined>} A single mapped metric group record.
 */
export const getMetricGroupByName = async (
  master_list: boolean,
  metric_group_name: string,
  group_id?: string,
  company_id?: string,
  deleted?: boolean
): Promise<MetricGroup | undefined> => {
  const metric_group: MetricGroup = await apollo_client
    .query({
      variables: {
        master_list: master_list,
        metric_group_name: metric_group_name,
        group_id: group_id,
        company_id: company_id,
        deleted: deleted
      },
      query: GET_METRIC_GROUP_QUERY,
      fetchPolicy: 'no-cache'
    })
    .then((response) => {
      return response.data.metric_group;
    });
  return metric_group;
};

/**
 * Create Metric Group with relative data
 * @param {boolean} master_list Flag to create new Metric Group within master list collection
 * @param {string | undefined} group_id Optional ID of Group which Metric Group is being created for
 * @param {string | undefined} company_id Optional ID of Company which Metric Group is being created for
 * @param {MetricGroupData} metric_group Data of new Metric Group to add to doc
 * @returns {DocumentReference}
 */
export const createMetricGroup = async (
  master_list: boolean,
  metric_group: MetricGroupData,
  group_id?: string,
  company_id?: string
): Promise<DocumentReference> => {
  try {
    const created_metric_group: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_METRIC_GROUP_MUTATOR,
        variables: {
          master_list: master_list,
          metric_group: metric_group,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.createMetricGroup;
      });
    return created_metric_group;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric_group.ts failed on an unknown error while calling createMetricGroup.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id,
        metric_group: metric_group
      },
      tracking_id
    );
  }
};

/**
 * Update Metric Group with relative data
 * @param {boolean} master_list Flag to update metric group in master list
 * @param {MetricGroup} updated_metric_group New metric group data to push into document
 * @param {MetricGroup} original_metric_group Old metric group data
 * @param {string} group_id Optional ID of Group to update metric group for
 * @param {string} company_id Optional ID of Company to update metric group for
 * @returns {void}
 */
export const updateMetricGroup = async (
  master_list: boolean,
  updated_metric_group: MetricGroup,
  original_metric_group: MetricGroup,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_METRIC_GROUP_MUTATOR,
        variables: {
          master_list: master_list,
          updated_metric_group: updated_metric_group,
          original_metric_group: original_metric_group,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.updateMetricGroup;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric_group.ts failed on an unknown error while calling updateMetricGroup.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_metric_group: updated_metric_group,
        original_metric_group: original_metric_group
      },
      tracking_id
    );
  }
};

/**
 * Soft delete a Metric Group and any related Metrics and Emission Factors in Firestore transaction
 * @param {boolean} master_list Flag to delete entities from their respective master lists
 * @param {string | undefined} group_id Optional Group to delete metric group from
 * @param {string | undefined} company_id Optional Company to delete metric group from
 * @param {string} metric_group_id ID of metric group to delete
 * @param {string} metric_group_name Name of metric group being deleted
 * @return {void}
 */
export const deleteMetricGroupWithMetrics = async (
  master_list: boolean,
  metric_group_id: string,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_METRIC_GROUP_WITH_METRICS_MUTATOR,
        variables: {
          master_list: master_list,
          metric_group_id: metric_group_id,
          group_id: group_id,
          company_id: company_id
        }
      })
      .then((response) => {
        return response.data.deleteMetricGroupWithMetrics;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric_group.ts failed on an unknown error while calling deleteMetricGroupWithMetrics.',
      {
        group_id: group_id,
        company_id: company_id,
        metric_group_id: metric_group_id
      },
      tracking_id
    );
  }
};
